import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useAppSelector } from '../../hooks.ts';
import { projectsReducer } from '../../store/reducers/projects.reducer.ts';
import { getFolderTreeSelector } from '../../store/selectors/projects.selector.ts';
import { Folder } from '../../types/folder.ts';
import FolderIconNew from './../../assets/FolderIconNew.svg?react';

const { loadFolderTree } = projectsReducer.actions;

interface MoveModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName?: string;
  selectedItemId: number;
  selectedItemName: string;
}

interface MoveProjectPayload {
  projectId: number;
  targetFolderId: number | null;
}

const MoveModal: React.FC<MoveModalProps> = ({ isOpen, onClose, selectedItemId, selectedItemName }) => {
  const dispatch = useDispatch();

  const folder = useAppSelector(getFolderTreeSelector);
  const { moveProject } = projectsReducer.actions;
  const [rootSelected, setRootSelected] = useState<boolean>(false);
  const [selectedFolder, setSelectedFolder] = useState<number | undefined>(undefined);

  const [folderStack, setFolderStack] = useState<Folder[]>([]);

  const handleEnterFolder = (folder: Folder) => {
    setFolderStack((prev) => [...prev, folder]);
  };

  const handleGoBack = () => {
    setFolderStack((prev) => prev.slice(0, -1));
  };

  const selectFolderClick = (folder: Folder) => {
    if (folder) {
      setSelectedFolder(folder.id);
      setRootSelected(false);
    }
  };

  const selectRootFolderClick = () => {
    setRootSelected(true);
    setSelectedFolder(undefined);
  };

  const handleSubmit = () => {
    if (selectedFolder && selectedItemId) {
      const payload: MoveProjectPayload = {
        projectId: selectedItemId,
        targetFolderId: selectedFolder,
      };

      dispatch(moveProject(payload));
      setSelectedFolder(undefined);
      onClose();
    } else if (rootSelected && selectedItemId) {
      const payload: MoveProjectPayload = {
        projectId: selectedItemId,
        targetFolderId: null,
      };

      dispatch(moveProject(payload));
      setSelectedFolder(undefined);
      setRootSelected(false);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedFolder(undefined);
    setRootSelected(false);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(loadFolderTree(undefined));
      setSelectedFolder(undefined);
      setRootSelected(false);
      setFolderStack([]);
    }
  }, [isOpen, dispatch]);

  const currentFolder = folderStack[folderStack.length - 1];
  const folders = folder ? [folder] : [];
  const foldersToDisplay = currentFolder
    ? currentFolder.children || []
    : folders && folders[0]
      ? folders[0].children
      : [];

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
        onClick={handleCancel}
      >
        <div className="bg-greyP5 rounded-md shadow-xl w-[602px] h-auto p-10" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between pb-2 border-b border-greyP4">
            <h2 className="text-white text-lg font-medium space-x-3">Move project {selectedItemName}</h2>
          </div>

          <div className="py-3 flex items-center justify-between">
            <div className="text-white text-md">
              <div className="text-white text-md flex items-center space-x-2">
                {folderStack.length > 0 && (
                  <button
                    onClick={handleGoBack}
                    className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs border border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500`}
                    style={{
                      height: '36px',
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      paddingLeft: '18px',
                      paddingRight: '18px',
                      gap: '8px',
                    }}
                  >
                    <ChevronLeft size={16} color="white" />
                    <span>Back</span>
                  </button>
                )}
                {folderStack.map((f, i) => (
                  <span key={f.id} className="text-gray-300">
                    {f.name}
                    {i < folderStack.length - 1 && ' / '}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="py-2 flex-1 overflow-y-scroll h-[200px] modal-content">
            <div>
              {folders &&
                folders.map((folder) => (
                  <div key={`folders-${folder.id}`}>
                    {folderStack.length == 0 && (
                      <div
                        key={`folder-${folder.id}`}
                        onClick={() => selectRootFolderClick()}
                        className={`flex items-center justify-between p-2 cursor-pointer transition-colors duration-200 border-b ${
                          rootSelected
                            ? 'bg-blackP2 border-b-green01'
                            : 'bg-transparent border-b-greyP4 hover:bg-blackP2 hover:border-b-black-600'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <FolderIconNew className="w-5 h-5 text-gray-400" />
                          <span className="text-white font-medium">Root folder</span>
                        </div>
                      </div>
                    )}

                    {foldersToDisplay.map((folder_child) => (
                      <div
                        key={folder_child.id}
                        onClick={() => selectFolderClick(folder_child)}
                        className={`flex items-center justify-between p-2 cursor-pointer transition-colors duration-200 border-b ${
                          selectedFolder && selectedFolder === folder_child.id
                            ? 'bg-blackP2 border-b-green01'
                            : 'bg-transparent border-b-greyP4 hover:bg-blackP2 hover:border-b-black-600'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <FolderIconNew className="w-5 h-5 text-gray-400" />
                          <span className="text-white font-medium">{folder_child.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400 text-sm">
                            {folder_child.children?.length || 0} folder
                            {(folder_child.children?.length || 0) !== 1 ? 's' : ''}
                          </span>
                          {folder_child.children && folder_child.children.length > 0 && (
                            <ChevronRight
                              onClick={(e) => {
                                e.stopPropagation(); // spreči selekciju foldera
                                handleEnterFolder(folder_child);
                              }}
                              className="w-4 h-4 text-gray-400 cursor-pointer hover:text-white"
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    {folder.children && folder.children.length === 0 && (
                      <div className="text-center text-gray-400 py-8">No folders available</div>
                    )}
                  </div>
                ))}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 p-6">
            <button
              onClick={handleCancel}
              className="px-6 py-2 text-white border border-white rounded-full hover:bg-white hover:text-black transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedFolder && !rootSelected}
              className="px-6 py-2 bg-white text-black font-medium rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MoveModal;
